import {Component, Input, OnInit, TemplateRef, ViewChild} from '@angular/core';
import {EMPTY, forkJoin, Observable, of} from 'rxjs';
import {catchError, finalize, map, switchMap, tap} from 'rxjs/operators';
import {HttpErrorResponse} from '@angular/common/http';
import {FuelTypeService} from '../shared/fuel-type.service';
import {LoaderService} from '../../shared/services/loader.service';
import {ModalService} from '../../shared/services/modal.service';
import {AuthenticationService} from '../../shared/services/authentication.service';
import {ErrorHandlerService} from '../../shared/services/error-handler.service';
import {Region} from '../../user-dashboard/shared/region.model';
import {FuelType, FuelTypeRegion} from '../shared/fuel-type.model';
import {ToastrService} from 'ngx-toastr';
import {TranslateService} from '@ngx-translate/core';


@Component({
  selector: 'app-decarbonization-fuel-type-list',
  templateUrl: './decarbonization-fuel-type-list.component.html',
  styleUrls: ['./decarbonization-fuel-type-list.component.scss']
})
export class DecarbonizationFuelTypeListComponent implements OnInit {

  public selfFuelTypes: Array<FuelType | FuelTypeRegion> = [];
  public filteredFuelTypes: Array<FuelType> = [];
  public removableFuelTypesId: Array<string> = [];

  public newFuelType: string;
  public selectedFuelType: FuelType;

  @Input() public regions: Array<Region> = [];
  @Input() public canCreateDecarbonizationTypes = false;
  @Input() public canManageRegionDecarbonizationTypes = false;

  @ViewChild('fuelTypeCreationModal') private fuelTypeCreationModal: TemplateRef<any>;
  @ViewChild('regionFuelTypeAddModal') private regionFuelTypeAddModal: TemplateRef<any>;
  @ViewChild('fuelTypeDeletionModal') private fuelTypeDeletionModal: TemplateRef<any>;
  @ViewChild('fuelTypeRegionRemovalModal') private fuelTypeRegionRemovalModal: TemplateRef<any>;

  constructor(
    private loaderService: LoaderService,
    private modalService: ModalService,
    private fuelTypeService: FuelTypeService,
    private authService: AuthenticationService,
    private errorHandlerService: ErrorHandlerService,
    private toastrService: ToastrService,
    private translationService: TranslateService
  ) {
  }

  ngOnInit(): void {
    this.refreshListObservable().subscribe();
  }

  public openFuelTypeCreationModal() {
    this.newFuelType = '';
    this.modalService.open({
      titleKey: 'decarbonization.fuel-types.creation-modal.title',
      content: this.fuelTypeCreationModal,
      validateButton: {
        textKey: 'buttons.validate',
      },
      closeButton: {
        textKey: 'buttons.cancel',
      },
      size: 'sm'
    })
      .pipe(
        switchMap(() => this.fuelTypeService.createFuelType(this.newFuelType)),
        catchError((e: HttpErrorResponse) => {
          this.errorHandlerService.handleHttpError(e, 'decarbonization.fuel-types.error.creation');
          return EMPTY;
        }),
      ).subscribe(
      () => {
        this.toastrService.success(
          this.translationService.instant('decarbonization.fuel-types.creation-modal.success')
        );
        this.refreshListObservable().subscribe();
      }
    );
  }

  public openRegionFuelTypeAddModal() {
    delete this.selectedFuelType;

    const regionNumberByFuelType = this.selfFuelTypes.reduce(
      (record, regionFuelType: FuelTypeRegion) => {
        if (record.hasOwnProperty(regionFuelType.fuelTypeId)) {
          return {...record, [regionFuelType.fuelTypeId]: ++record[regionFuelType.fuelTypeId]}
        } else {
          return {...record, [regionFuelType.fuelTypeId]: 1}
        }
      },
      {}
    )

    return this.fuelTypeService.getFuelTypes()
      .pipe(
        map(fuelTypes => fuelTypes.filter(fuelType => regionNumberByFuelType[fuelType.id] == null || regionNumberByFuelType[fuelType.id] < this.regions.length)),
        tap(filteredFuelTypes => this.filteredFuelTypes = filteredFuelTypes),
        switchMap(() => this.modalService.open({
          titleKey: 'decarbonization.fuel-types.region-add.title',
          content: this.regionFuelTypeAddModal,
          validateButton: {
            textKey: 'buttons.validate',
          },
          closeButton: {
            textKey: 'buttons.cancel',
          },
          size: 'sm'
        })),
        switchMap(() => this.fuelTypeService.addOwnRegionFuelType(this.selectedFuelType)),
        catchError((e: HttpErrorResponse) => {
          this.errorHandlerService.handleHttpError(e, 'decarbonization.fuel-types.error.add-region');
          return EMPTY;
        }),
      ).subscribe(() => {
          this.toastrService.success(
            this.translationService.instant('decarbonization.fuel-types.region-add.success')
          );
          this.refreshListObservable().subscribe();
        }
      );
  }

  public openFuelTypeDeletionModal(fuelType: FuelType) {
    this.modalService.open({
      titleKey: 'decarbonization.fuel-types.deletion-modal.title',
      content: this.fuelTypeDeletionModal,
      validateButton: {
        textKey: 'buttons.validate',
      },
      closeButton: {
        textKey: 'buttons.cancel',
      },
      size: 'sm'
    })
      .pipe(
        switchMap(() => this.fuelTypeService.deleteFuelType(fuelType)),
        catchError((e: HttpErrorResponse) => {
          this.errorHandlerService.handleHttpError(e, 'decarbonization.fuel-types.deletion-modal.error');
          return EMPTY;
        }),
      ).subscribe(
      () => {
        this.toastrService.success(
          this.translationService.instant('decarbonization.fuel-types.deletion-modal.success')
        );
        this.refreshListObservable().subscribe();
      }
    );
  }

  public openFuelTypeRegionRemovalModal(fuelTypeRegion: FuelTypeRegion) {
    this.modalService.open({
      titleKey: 'decarbonization.fuel-types.remove-region-modal.title',
      content: this.fuelTypeRegionRemovalModal,
      validateButton: {
        textKey: 'buttons.validate',
      },
      closeButton: {
        textKey: 'buttons.cancel',
      },
      size: 'sm'
    })
      .pipe(
        switchMap(() => this.fuelTypeService.deleteOwnRegionFuelType(fuelTypeRegion)),
        catchError((e: HttpErrorResponse) => {
          this.errorHandlerService.handleHttpError(e, 'decarbonization.fuel-types.remove-region-modal.error');
          return EMPTY;
        }),
      ).subscribe(
      () => {
        this.toastrService.success(
          this.translationService.instant('decarbonization.fuel-types.remove-region-modal.success')
        );
        this.refreshListObservable().subscribe();
      }
    );
  }

  private refreshListObservable() {
    this.loaderService.startLoad();
    return forkJoin([this.getFuelTypeListObservable(), this.getRemovableFuelTypeListObservable()])
      .pipe(
        finalize(() => this.loaderService.stopLoad())
      );
  }

  private getFuelTypeListObservable () {
    let fuelTypeListObservable: Observable<any[]>;
    if (this.canCreateDecarbonizationTypes) {
      fuelTypeListObservable = this.fuelTypeService.getFuelTypes();
    } else if (this.canManageRegionDecarbonizationTypes) {
      fuelTypeListObservable = this.fuelTypeService.getSelfFuelTypes();
    } else {
      throw Error('Cannot fetch fuel types list');
    }

    return fuelTypeListObservable.pipe(
      catchError((e: HttpErrorResponse) => {
        this.errorHandlerService.handleHttpError(e, 'decarbonization.fuel-types.error.fetch');
        return EMPTY;
      }),
      tap(fuelTypes => this.selfFuelTypes = fuelTypes)
    )
  }

  private getRemovableFuelTypeListObservable() {
    let removableFuelTypeListObservable: Observable<any[]>;
    if (this.canCreateDecarbonizationTypes) {
      removableFuelTypeListObservable = this.fuelTypeService.getRemovableFuelTypeIds();
    } else if (this.canManageRegionDecarbonizationTypes) {
      removableFuelTypeListObservable = this.fuelTypeService.getSelfRemovableFuelTypeRegionIds();
    } else {
      removableFuelTypeListObservable = of([]);
    }

    return removableFuelTypeListObservable.pipe(
      tap(removableFuelTypes => this.removableFuelTypesId = removableFuelTypes)
    );
  }
}
